import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import { loginSuccess, logout } from "../../state/auth";
import { jwtDecode } from "jwt-decode";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt,
  faUsers,
  faBoxOpen,
  faRocket,
  faUserCircle,
  faGift,
  faTrophy,
  faEnvelope,
  faTh,
  faSignOutAlt,
  faSignInAlt,
  faBars,
  faTimes,
  faChartLine,
  faUsersCog,
} from '@fortawesome/free-solid-svg-icons';
import { faTwitch } from '@fortawesome/free-brands-svg-icons';

const AdminHeader = () => {
  const [isMenuVisible, setMenuVisible] = useState(false);
  const toggleMenu = () => setMenuVisible((prev) => !prev);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const dispatch = useAppDispatch();
  const { isLoggedIn, nickname } = useAppSelector((state) => state.authSlice);
  const [token, setToken] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const navigate = useNavigate();

  const navLinks = [
    { to: '/admin', icon: faTachometerAlt, label: 'Dashboard' },
    { to: '/manageuser', icon: faUsers, label: 'User Management' },
    { to: '/adminassetsview', icon: faBoxOpen, label: 'Assets View' },
    { to: '/adminboostingview', icon: faRocket, label: 'Boosting View', debug: true },
    { to: '/adminprofile', icon: faUserCircle, label: 'Admin Profile', debug: true },
    { to: '/managegiveaway', icon: faGift, label: 'Manage Giveaway' },
    { to: '/managetwitchmarketors', icon: faTwitch, label: 'Twitch Marketors' },
    { to: '/managewinners', icon: faTrophy, label: 'Manage Winners' },
    { to: '/mailsmtp', icon: faEnvelope, label: 'Mail SMTP' },
    { to: '/boostingsalemanage', icon: faChartLine, label: 'Boosting Sales', debug: true },
    { to: '/accountssalesmanage', icon: faUsersCog, label: 'Accounts Sales', debug: true },
    { to: '/all', icon: faTh, label: 'All', debug: true },
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem("admin_token");
    if (storedToken && !isLoggedIn) {
        try {
            const decodedToken: { name: string, exp: number } = jwtDecode(storedToken);
            if (Date.now() < decodedToken.exp * 1000) {
                dispatch(loginSuccess(decodedToken.name));
            } else {
                localStorage.removeItem("admin_token");
            }
        } catch (e) {
            console.error("Invalid token:", e);
            localStorage.removeItem("admin_token");
        }
    }
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    const currentToken = localStorage.getItem("admin_token");
    console.log('Admin token:', currentToken);
    if (isLoggedIn) {
        setUserName(nickname);
    } else {
        setUserName(null);
    }
    setToken(currentToken);
  }, [isLoggedIn, nickname]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    dispatch(logout());
  };

  return (
    <>
      <div
        className={` shadow-header sticky top-0 z-[1505] flex w-full h-[34px] md:h-[51px] lg:h-[51px] bg-[#090909] items-center px-1 py-4 md:px-10 justify-between -mt-1`}
      >
        <div className="flex items-center gap-1 md:gap-4">
          <Link to="/iplaymore">
            <img
              alt="header-logo"
              src="assets/img/logo-sm.png"
              className="w-[75px] lg:w-[86px] h-[28px] lg:h-[32px]"
            />
          </Link>
        </div>
        <div className="flex items-center gap-1 md:gap-4">
          <Link to="/admin">
            <h1 className="text-white text-2xl font-bold">Admin</h1>
          </Link>
        </div>
        <FontAwesomeIcon icon={faBars} className="w-[29px] pr-2 cursor-pointer" onClick={toggleMenu} />
        {isMenuVisible && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[1499] p-4 pt-20 overflow-y-auto">
            <button
              onClick={toggleMenu}
              className="absolute top-4 right-4 text-white hover:text-gray-300"
            >
              <FontAwesomeIcon icon={faTimes} className="w-8 h-8" />
            </button>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={toggleMenu}
                  className="relative flex flex-col items-center justify-center gap-2 p-4 bg-[#090909] border border-[#666666] rounded-lg text-white text-center hover:bg-[#222] transition-colors"
                >
                  {(link as any).debug && (
                    <div style={{
                      position: 'absolute',
                      top: '0',
                      right: '0',
                      backgroundColor: 'red',
                      color: 'white',
                      padding: '2px 6px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      borderBottomLeftRadius: '8px'
                    }}>
                      debug
                    </div>
                  )}
                  <FontAwesomeIcon icon={link.icon} className="w-8 h-8" />
                  <span className="text-sm font-medium">{link.label}</span>
                </Link>
              ))}
              {token ? (
                <button
                  onClick={() => { handleLogout(); toggleMenu(); }}
                  className="flex flex-col items-center justify-center gap-2 p-4 bg-[#090909] border border-[#666666] rounded-lg text-white text-center hover:bg-[#222] transition-colors"
                >
                  <FontAwesomeIcon icon={faSignOutAlt} className="w-8 h-8" />
                  <span className="text-sm font-medium">Sign out</span>
                </button>
              ) : (
                <button
                  onClick={() => { navigate('/adminlogin'); toggleMenu(); }}
                  className="flex flex-col items-center justify-center gap-2 p-4 bg-[#090909] border border-[#666666] rounded-lg text-white text-center hover:bg-[#222] transition-colors"
                >
                  <FontAwesomeIcon icon={faSignInAlt} className="w-8 h-8" />
                  <span className="text-sm font-medium">Sign in</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminHeader;
